import { graphql } from '@gql';

export const GET_ALL_PROJECTS = graphql(`
    query GetProjects(
        $where: ProjectWhereInput
        $field: ProjectOrderField
        $direction: OrderDirection!
    ) {
        projects(
            where: $where
            orderBy: { field: $field, direction: $direction }
        ) {
            totalCount
            edges {
                node {
                    id
                    name
                    description
                    creator {
                        id
                        gid
                        firstName
                        lastName
                        email
                    }
                    organization {
                        id
                        name
                    }
                    milestone {
                        id
                        name
                        projectPhase {
                            id
                            name
                        }
                    }
                    status {
                        id
                        name
                    }
                    type {
                        id
                        name
                    }
                    ...ProjectCustomerPartial
                    ...ProjectLocationPartial
                    ...ProjectPlantPartial
                    ...ProjectProfilePartial
                    projectCountries {
                        country {
                            id
                        }
                    }
                    updateTime
                    createTime
                }
            }
        }
    }
`);

export const GET_PROJECT_ZONES_IN_PROJECT = graphql(`
    query GetProjectZonesInProject($pid: ID!) {
        projectZones(
            where: { projectID: $pid }
            orderBy: { field: NAME, direction: ASC }
        ) {
            edges {
                node {
                    id
                    name
                }
            }
        }
    }
`);

export const GET_UNORDERED_PROJECT_ZONES_IN_PROJECT = graphql(`
    query GetUnorderedProjectZonesInProject($pid: ID!) {
        projectZones(where: { projectID: $pid }) {
            edges {
                node {
                    id
                    name
                }
            }
        }
    }
`);

export const GET_ALL_ZONES_IN_PROJECT = graphql(`
    query listProjectZones($pid: ID!) {
        projectZones(where: { projectID: $pid }) {
            edges {
                node {
                    ...ProjectZoneFragment
                }
            }
        }
    }
`);

export const GET_PROJECT_COMPONENTS = graphql(`
    query GetProjectComponents(
        $pid: ID!
        $orderBy: ComponentInProjectOrder
        $where: ComponentInProjectWhereInput
        $first: Int
        $before: Cursor
        $after: Cursor
    ) {
        componentsInProject(
            projectID: $pid
            orderBy: $orderBy
            where: $where
            first: $first
            before: $before
            after: $after
        ) {
            edges {
                node {
                    id
                    name
                    componentVendorName
                    version
                    isValid
                    cpeName
                    eolDate
                    eolReached
                    monitoredSince
                    usedInProjectCount
                    url
                    securityURL
                    createTime
                    lastSync
                    projectID
                    updateTime
                    hasAssessments
                }
            }
            totalCount
            pageInfo {
                startCursor
                endCursor
            }
        }
    }
`);

export const GET_PROJECTS_AFFECTED_BY_NOTIFICATION = graphql(`
    query GetProjectsAffectedByNotification($notificationId: ID!) {
        projectsAffectedByNotification(notificationID: $notificationId) {
            edges {
                node {
                    id
                    name
                    description
                    affectedComponentsCount
                    affectedAssetsCount
                }
            }
        }
    }
`);

export const GET_COMPONENTS_AFFECTED_BY_ANY_NOTIFICATIONS = graphql(`
    query GetComponentsAffectedByAnyNotifications(
        $pid: ID!
        $first: Int
        $orderBy: ComponentAffectedByNotificationProjectOrder
    ) {
        componentsAffectedByNotificationInProject(
            where: { projectID: $pid }
            first: $first
            orderBy: $orderBy
        ) {
            edges {
                node {
                    id
                    name
                    affectedAssetsCount
                }
            }
        }
    }
`);

export const GET_MITIGATIONS_SUMMARY_IN_PROJECT = graphql(`
    query getMitigationsSummaryInProject($pid: ID!) {
        mitigations(
            where: {
                hasAssetItemWith: [
                    {
                        hasAssetWith: [
                            { hasProjectZoneWith: [{ projectID: $pid }] }
                        ]
                    }
                ]
            }
        ) {
            totalCount
        }
    }
`);

export const GET_SINGLE_PROJECT_NAME_ONLY = graphql(`
    query GetProjectName($pid: ID!) {
        projects(where: { id: $pid }) {
            edges {
                node {
                    name
                }
            }
        }
    }
`);

export const GET_PROJECT_TASKS_SUMMARY = graphql(`
    query GetProjectTasksSummary($pid: ID!) {
        tasks(where: { projectID: $pid }) {
            edges {
                node {
                    id
                    status
                }
            }
        }
    }
`);

export const GET_PROJECTS_TASKS_SUMMARY = graphql(`
    query GetProjectsTasksSummary($projectIDs: [ID!]!) {
        tasks(where: { projectIDIn: $projectIDs }) {
            edges {
                node {
                    id
                    status
                    project {
                        id
                    }
                }
            }
        }
    }
`);

export const GET_ZONES_COUNT_FOR_PROJECT = graphql(`
    query GetZonesCountForProject($pid: ID!) {
        projectZones(where: { projectID: $pid }) {
            totalCount
        }
    }
`);

export const GET_TRAS_FOR_PROJECT_SUMMARY = graphql(`
    query trasForProjectSummary($pid: ID!) {
        tras(where: { projectID: $pid }) {
            totalCount
            edges {
                node {
                    status
                }
            }
        }
    }
`);

export const GET_ASSETS_COUNT_FOR_PROJECT = graphql(`
    query GetAssetsCountForProject($pid: ID!) {
        assetsInProject(projectID: $pid) {
            totalCount
            edges {
                node {
                    assetItems {
                        id
                    }
                }
            }
        }
    }
`);

export const GET_COMPONENTS_COUNT_FOR_PROJECT = graphql(`
    query GetComponentsCountForProject($pid: ID!) {
        componentsInProject(projectID: $pid) {
            totalCount
        }
    }
`);

export const GET_SBOM_STATS_FOR_PROJECT = graphql(`
    query GetSbomStatsForProject($pid: ID!) {
        componentsInProject(projectID: $pid) {
            totalCount
        }
        assetItems(
            where: { hasAssetWith: { hasProjectZoneWith: { projectID: $pid } } }
        ) {
            totalCount
        }
        projectComponentsNotifications(projectID: $pid) {
            totalCount
        }
    }
`);

export const GET_PROJECT_USERS_ROLES = graphql(`
    query GetProjectUsersRoles($pid: ID!) {
        projects(where: { id: $pid }) {
            edges {
                node {
                    users {
                        user {
                            id
                        }
                        role {
                            id
                            name
                        }
                    }
                }
            }
        }
    }
`);

export const GET_PROJECT_SUMMARY = graphql(`
    query GetProjectDetails($pid: ID!) {
        projects(where: { id: $pid }) {
            edges {
                node {
                    ...ProjectSummary
                }
            }
        }
    }
`);

export const GET_PROJECT_MARKET_COUNTRIES = graphql(`
    query GetProjectMarketCountries($pid: ID!) {
        projects(where: { id: $pid }) {
            edges {
                node {
                    id
                    projectCountries {
                        country {
                            id
                            name
                        }
                    }
                }
            }
        }
    }
`);

export const ProjectUserParticipantFragment = graphql(`
    fragment ProjectUserParticipant on ProjectUser {
        id
        user {
            id
            firstName
            lastName
            email
        }
        role {
            id
            name
        }
    }
`);

export const OrganizationUserParticipantFragment = graphql(`
    fragment OrganizationUserParticipant on OrganizationUser {
        id
        user {
            id
            firstName
            lastName
            email
        }
        organization {
            id
            name
        }
        organizationRole: role
    }
`);

export const GET_PARTICIPANTS_FOR_PROJECT = graphql(`
    query GetParticipantsForProject($pid: ID!) {
        projects(where: { id: $pid }) {
            edges {
                node {
                    id
                    name
                    creator {
                        id
                    }
                    organization {
                        id
                        name
                    }
                    participants {
                        ... on ProjectUser {
                            ...ProjectUserParticipant
                        }
                        ... on OrganizationUser {
                            ...OrganizationUserParticipant
                        }
                    }
                }
            }
        }
    }
`);

export const GET_USERS_FOR_PROJECT = graphql(`
    query GetUsersForProject($pid: ID!) {
        projects(where: { id: $pid }) {
            edges {
                node {
                    users {
                        ...ProjectUserPartial
                    }
                }
            }
        }
    }
`);

export const GET_PROJECT_GUIDANCE_CONFIGS = graphql(`
    query GetProjectGuidanceConfigs {
        projectGuidances {
            edges {
                node {
                    id
                    name
                    createTime
                    updateTime
                    isPublished
                    version
                    projectType {
                        id
                        name
                    }
                    standardProfile {
                        ...StandardProfileFragment
                    }
                }
            }
        }
    }
`);

export const GET_PROJECT_GUIDANCES_BY_PROJECT_TYPE = graphql(`
    query GetProjectGuidancesByProjectType($projectTypeId: ID!) {
        projectGuidances(
            where: { projectTypeID: $projectTypeId, isPublished: true }
            orderBy: { direction: DESC, field: VERSION }
        ) {
            edges {
                node {
                    id
                    standardProfile {
                        ...StandardProfileFragment
                    }
                }
            }
        }
    }
`);

export const GET_PROJECT_TYPES = graphql(`
    query GetProjectTypes {
        projectTypes {
            edges {
                node {
                    id
                    name
                }
            }
        }
    }
`);

export const GET_PROJECT_SEARCH_FILTERS = graphql(`
    query GetProjectSearchFilters {
        projectStatuses {
            edges {
                node {
                    id
                    name
                }
            }
        }
        projectTypes {
            edges {
                node {
                    id
                    name
                }
            }
        }
    }
`);

export const RelatedFormFieldQuery = graphql(`
    query ProjectFormFields {
        organizations(orderBy: { field: NAME, direction: ASC }) {
            edges {
                node {
                    id
                    name
                    parentOrganization {
                        id
                    }
                }
            }
        }
        projectStatuses {
            edges {
                node {
                    id
                    name
                }
            }
        }
        projectTypes {
            edges {
                node {
                    id
                    name
                }
            }
        }
        projectMilestones {
            edges {
                node {
                    id
                    name
                    seqNo
                }
            }
        }
        users {
            edges {
                node {
                    id
                    firstName
                    lastName
                }
            }
        }
    }
`);

export const GET_PROJECT_MILESTONES_BY_PROJECT_TYPE_ID = graphql(`
    query GetProjectMilestonesByProjectTypeId($projectTypeId: ID!) {
        projectMilestones(
            where: { hasProjectPhaseWith: { projectTypeID: $projectTypeId } }
        ) {
            edges {
                node {
                    id
                    name
                    seqNo
                }
            }
        }
    }
`);

export const GET_PROJECT_STATUSES_AND_MILESTONES = graphql(`
    query GetProjectStatusesAndMilestones($projectTypeId: ID!) {
        projectStatuses {
            edges {
                node {
                    id
                    name
                }
            }
        }
        projectMilestones(
            where: { hasProjectPhaseWith: { projectTypeID: $projectTypeId } }
        ) {
            edges {
                node {
                    id
                    name
                    seqNo
                }
            }
        }
    }
`);

export const GET_PROJECT_FOR_GUIDANCE = graphql(`
    query GetProjectForGuidance($pid: ID!) {
        projects(where: { id: $pid }) {
            edges {
                node {
                    id
                    milestone {
                        id
                        name
                        projectPhase {
                            id
                        }
                    }
                    type {
                        id
                        name
                    }
                }
            }
        }
    }
`);

export const GET_PROJECT_GUIDANCE = graphql(`
    query GetProjectGuidance($projectTypeId: ID!) {
        projectPhases(where: { projectTypeID: $projectTypeId }) {
            edges {
                node {
                    id
                    name
                    seqNo
                    projectTypeID
                }
            }
        }
        projectMilestones(
            where: { hasProjectPhaseWith: { projectTypeID: $projectTypeId } }
        ) {
            edges {
                node {
                    id
                    name
                    seqNo
                    description
                    projectPhase {
                        id
                        name
                        seqNo
                        projectTypeID
                    }
                }
            }
        }
    }
`);

export const GET_PROJECT_PHASES_AND_MILESTONES = graphql(`
    query GetProjectPhasesAndMilestones($projectTypeId: ID!) {
        projectPhases(where: { projectTypeID: $projectTypeId }) {
            edges {
                node {
                    id
                    name
                    projectTypeID
                    seqNo
                    projectMilestones {
                        id
                        name
                        description
                        seqNo
                    }
                }
            }
        }
    }
`);

export const GET_PROJECTS_STATS = graphql(`
    query projectsStats($pid: ID!) {
        pendingTasks: tasks(
            where: {
                and: [{ projectID: $pid }, { not: { status: COMPLETED } }]
            }
        ) {
            totalCount
        }
        assets(where: { hasProjectZoneWith: { projectID: $pid } }) {
            totalCount
        }
        components: componentsInProject(projectID: $pid) {
            totalCount
        }
    }
`);

export const GET_PROJECT_RESOURCES = graphql(`
    query GetProjectResources($pid: ID!) {
        projectZones(
            where: { projectID: $pid }
            orderBy: { field: NAME, direction: ASC }
        ) {
            edges {
                node {
                    ...ProjectZoneFragment
                }
            }
        }
        assets(where: { hasProjectZoneWith: { projectID: $pid } }) {
            totalCount
            edges {
                node {
                    ...AssetBaseFragment
                    assetItems {
                        id
                        assetItemAttributes {
                            ...AssetItemAttributeFragment
                        }
                    }
                }
            }
        }
        assetAttributeDefinitionsForProject(projectID: $pid) {
            edges {
                node {
                    ...AttributeDefinitionFragment
                    assetType {
                        id
                        name
                    }
                    assetAttributes {
                        ...AssetAttributeFragment
                    }
                }
            }
        }
        assetRelationsForProject(projectID: $pid) {
            edges {
                node {
                    id
                    name
                    description
                    type
                    sourceAsset {
                        id
                    }
                    targetAsset {
                        id
                    }
                    assetRelationAttributes {
                        value
                        assetRelationAttributeDefinition {
                            id
                            label
                            type
                            objectType
                        }
                    }
                }
            }
        }
    }
`);

export const GET_PROJECT_ATTRIBUTES = graphql(`
    query GetProjectAttributes($pid: ID!) {
        projects(where: { id: $pid }) {
            edges {
                node {
                    id
                    projectAttributes {
                        id
                        label
                        sequence
                        type
                        value
                    }
                }
            }
        }
    }
`);

export const GET_PROJECT_HIERARCHIES = graphql(`
    query GetProjectHierarchies($pid: ID!) {
        projects(where: { id: $pid }) {
            edges {
                node {
                    id
                    projectHierarchies {
                        ...ProjectHierarchyPartial
                    }
                }
            }
        }
    }
`);

export const GET_PROJECT_SYSTEMS_MENU = graphql(`
    query GetProjectSystemsMenu($pid: ID!) {
        projectHierarchies(where: { projectID: $pid }) {
            edges {
                node {
                    id
                    name
                    parentHierarchy {
                        id
                    }
                }
            }
        }
    }
`);

export const GET_PROJECT_SYSTEMS = graphql(`
    query GetProjectSystems($pid: ID!) {
        projectHierarchies(where: { projectID: $pid }) {
            edges {
                node {
                    ...ProjectHierarchyPartial
                }
            }
        }
    }
`);

export const GET_PROJECT_CRA_COUNT = graphql(`
    query GetProjectCraCount($pid: ID!) {
        crasForProject(projectID: $pid) {
            id
        }
    }
`);
